import { MeetingMinuteReviewHistoryStatus, MeetingMinuteReviewStatus, MeetingMinuteStatus } from '@/enum/global.enum'
import { getGlobalStatusColor, getGlobalStatusHexColor } from '@/utils/global-status-color.utils'

/**
 * Converts meeting minute status identifiers to human-readable display labels
 *
 * @param status - The meeting minute status identifier to convert. Can be a MeetingMinuteStatus enum value, string, null, or undefined
 * @returns A human-readable string representing the meeting minute status. Returns 'xxxx' for unknown/null statuses
 */
const meetingMinuteStatusLabel = (status?: MeetingMinuteStatus | null | string) => {
  if (!status) return 'xxxx'

  switch (status) {
    case MeetingMinuteStatus.DRAFT:
      return 'Draft'
    case MeetingMinuteStatus.REVIEW:
      return 'Review'
    case MeetingMinuteStatus.APPROVED:
      return 'Approved'
    case MeetingMinuteStatus.COMPLETED:
      return 'Completed'
    case MeetingMinuteStatus.CANCELLED:
      return 'Cancelled'
    case MeetingMinuteStatus.ARCHIVED:
      return 'Archived'
    default:
      return 'xxxx'
  }
}

const meetingMinuteStatusColor = (status?: MeetingMinuteStatus | null | string) => {
  return getGlobalStatusColor(status)
}

const meetingMinuteStatusBgColorOptions: Partial<Record<MeetingMinuteStatus, string>> = {
  [MeetingMinuteStatus.DRAFT]: getGlobalStatusHexColor(MeetingMinuteStatus.DRAFT),
  [MeetingMinuteStatus.REVIEW]: getGlobalStatusHexColor(MeetingMinuteStatus.REVIEW),
  [MeetingMinuteStatus.APPROVED]: getGlobalStatusHexColor(MeetingMinuteStatus.APPROVED),
  [MeetingMinuteStatus.COMPLETED]: getGlobalStatusHexColor(MeetingMinuteStatus.COMPLETED),
  [MeetingMinuteStatus.CANCELLED]: getGlobalStatusHexColor(MeetingMinuteStatus.CANCELLED),
  [MeetingMinuteStatus.ARCHIVED]: getGlobalStatusHexColor(MeetingMinuteStatus.ARCHIVED),
}

const MEETING_MINUTE_WORKFLOW_ORDER: MeetingMinuteStatus[] = [
  MeetingMinuteStatus.DRAFT,
  MeetingMinuteStatus.REVIEW,
  MeetingMinuteStatus.APPROVED,
  MeetingMinuteStatus.COMPLETED,
]

const isUnusualMeetingMinuteStatusChange = (
  currentStatus?: MeetingMinuteStatus | null,
  nextStatus?: MeetingMinuteStatus | null
): boolean => {
  if (!currentStatus || !nextStatus || currentStatus === nextStatus) return false

  if (currentStatus === MeetingMinuteStatus.DRAFT) return false

  if (currentStatus === MeetingMinuteStatus.CANCELLED || currentStatus === MeetingMinuteStatus.ARCHIVED) return true

  const currentIndex = MEETING_MINUTE_WORKFLOW_ORDER.indexOf(currentStatus)
  const nextIndex = MEETING_MINUTE_WORKFLOW_ORDER.indexOf(nextStatus)

  if (nextIndex === -1) return false

  return nextIndex < currentIndex
}

const meetingMinuteReviewHistoryStatusLabel = (status?: MeetingMinuteReviewHistoryStatus | null | string) => {
  if (!status) return 'xxxx'

  switch (status) {
    case MeetingMinuteReviewHistoryStatus.APPROVED:
      return 'Approved'
    case MeetingMinuteReviewHistoryStatus.REJECTED:
      return 'Rejected'
    default:
      return 'xxxx'
  }
}

const meetingMinuteReviewHistoryStatusColor = (status?: MeetingMinuteReviewHistoryStatus | null | string) => {
  return getGlobalStatusColor(status)
}

const meetingMinuteReviewStatusLabel = (status: MeetingMinuteReviewStatus) => {
  switch (status) {
    case MeetingMinuteReviewStatus.PENDING:
      return 'Pending Review'
    case MeetingMinuteReviewStatus.APPROVED:
      return 'Approved'
    case MeetingMinuteReviewStatus.REJECTED:
      return 'Rejected'
    default:
      return status
  }
}

export {
  isUnusualMeetingMinuteStatusChange,
  meetingMinuteReviewHistoryStatusColor,
  meetingMinuteReviewHistoryStatusLabel,
  meetingMinuteReviewStatusLabel,
  meetingMinuteStatusBgColorOptions,
  meetingMinuteStatusColor,
  meetingMinuteStatusLabel,
}
